import { getPageImage } from "@/lib/page-imagery";
import { EditorialHero } from "./EditorialHero";
import { LangLink } from "@/components/LangLink";
import {
  ClipboardCheck,
  Leaf,
  ShieldCheck,
  Fuel,
  Wheat,
  Boxes,
  Check,
  FileSearch,
  ClipboardList,
  ArrowLeft,
  Building2,
  Globe2,
  Layers,
  KeyRound,
  type LucideIcon,
} from "lucide-react";
import { content, whatsappUrl } from "@/lib/content";
import { vendors } from "@/lib/vendors";
import { industries } from "@/lib/industries";
import { titleWithLabel } from "@/lib/services-meta";
import { BuyerGuides } from "./BuyerGuides";
import { FaqSection } from "@/components/FaqSection";
import { useLanguage } from "@/lib/language";

type ServiceItem = (typeof content.services.items)[number];

const slugMeta: Record<string, { icon: LucideIcon }> = {
  "iso-9001": { icon: ClipboardCheck },
  "iso-14001": { icon: Leaf },
  "iso-45001": { icon: ShieldCheck },
  "iso-29001": { icon: Fuel },
  "iso-22000": { icon: Wheat },
  "iso-27001": { icon: KeyRound },
  "ministry-of-oil-vendor-registration": {
    icon: Boxes,
  },
  "oil-gas-registration": { icon: Fuel },
};

const stageIcons: LucideIcon[] = [FileSearch, ClipboardList];

function Reveal({
  children,
  className,
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  return <div className={className}>{children}</div>;
}

export function ServiceDetail({ item }: { item: ServiceItem }) {
  const { t, dir, lang } = useLanguage();
  const s = content.services;
  const isIso = item.slug.startsWith("iso-");
  const quotationUrl = isIso
    ? `${whatsappUrl}?text=${encodeURIComponent(
        t({
          en: `Hello IraqCert, I would like a quotation for ${item.label} preparation support. Company activity: . City and number of sites: . Buyer requirement and deadline (if any): .`,
          ar: `مرحباً عراق سيرت، أرغب في عرض سعر لدعم التحضير لمواصفة ${item.label}. نشاط الشركة: . المدينة وعدد المواقع: . متطلبات الجهة الطالبة والموعد المطلوب (إن وجد): .`,
        }),
      )}`
    : `${whatsappUrl}?text=${encodeURIComponent(
        t({
          en: `Hello IraqCert, I would like a registration route and dossier review for ${item.title.en}. Company: . Country of incorporation: . Activity: . Buyer or project: . Current application or buyer-request status (optional): . Target date: . Business contact: .\nPage: /services/${item.slug}`,
          ar: `مرحباً عراق سيرت، أرغب في مراجعة مسار التسجيل والملف لخدمة ${item.title.ar}. الشركة: . بلد التأسيس: . النشاط: . الجهة المشترية أو المشروع: . حالة طلب التسجيل أو طلب الجهة المشترية الحالي (اختياري): . الموعد المستهدف: . وسيلة تواصل العمل: .\nالصفحة: /ar/services/${item.slug}`,
        }),
      )}`;
  const displayLabel =
    lang === "ar"
      ? item.slug === "ministry-of-oil-vendor-registration"
        ? "وزارة النفط العراقية"
        : item.slug === "oil-gas-registration"
          ? "النفط والغاز"
          : item.label
      : item.label;
  const meta = slugMeta[item.slug];
  const photo = getPageImage(`/services/${item.slug}`);
  const Icon = meta.icon;
  const isAvl = item.slug === "ministry-of-oil-vendor-registration";
  const isOilGasReg = item.slug === "oil-gas-registration";
  const isEnergy = [
    "ministry-of-oil-vendor-registration",
    "iso-29001",
    "oil-gas-registration",
  ].includes(item.slug);
  const groupIcons: LucideIcon[] = [Building2, Globe2, Layers];

  return (
    <>
      {/* HERO */}
      <EditorialHero image={photo.src} alt={t(photo.alt)}>
        <LangLink
          to="/services"
          className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className={`size-4 ${dir === "rtl" ? "-scale-x-100" : ""}`} />
          {t(s.backToServices)}
        </LangLink>
        <div className="mt-6 max-w-xl text-start">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary-foreground">
            <Icon className="size-4" />
            {displayLabel}
          </span>
          <h1 className="mt-6 text-4xl font-semibold leading-tight text-foreground sm:text-5xl">
            {titleWithLabel(item.label, t(item.title), lang)}
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            {t(item.heroSubtitle)}
          </p>
          <a
            href={quotationUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="ic-button mt-7"
          >
            {isIso
              ? t({
                  en: `Discuss ${item.label} preparation on WhatsApp`,
                  ar: `ناقش التحضير لمواصفة ${item.label} عبر واتساب`,
                })
              : t({
                  en: "Discuss the registration route on WhatsApp",
                  ar: "ناقش مسار التسجيل عبر واتساب",
                })}
          </a>
        </div>
      </EditorialHero>

      {/* BUYER REQUIREMENTS — TYPOGRAPHY LOGO STRIP (directly below hero) */}
      {isEnergy && (
        <section className="bg-white text-zinc-900">
          <div className="mx-auto max-w-none w-full px-4 lg:px-8 py-16 sm:py-20">
            <Reveal className="text-center">
              <div className="flex items-center justify-center gap-3">
                <span className="inline-flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                  <KeyRound className="size-5" />
                </span>
                <h2 className="text-xl font-extrabold text-zinc-900 sm:text-2xl">
                  {t(s.biddingAccess.title)}
                </h2>
              </div>
              <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground">
                {t(s.biddingAccess.text)}
              </p>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-5 sm:gap-x-10 md:gap-x-14">
                {s.biddingAccess.energyLogos.map((logo) => (
                  <span
                    key={logo}
                    className="max-w-full cursor-default break-words text-center font-black uppercase tracking-[0.12em] text-zinc-900 transition-colors hover:text-black text-base sm:text-lg md:text-2xl sm:tracking-[0.2em]"
                  >
                    {logo}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* TARGETED ENERGY VENDOR LISTS — dark strip (AVL only) */}
      {isAvl && (
        <>
          <section className="bg-topbar text-topbar-foreground">
            <div className="mx-auto max-w-none w-full px-4 lg:px-8 pb-24 pt-4 sm:pb-28">
              <Reveal className="mx-auto max-w-2xl text-center">
                <span className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">
                  {t(s.energyVendors.eyebrow)}
                </span>
                <h2 className="mt-3 text-3xl font-extrabold text-background sm:text-4xl">
                  {t(s.energyVendors.title)}
                </h2>
                <p className="mx-auto mt-4 max-w-xl text-topbar-foreground/70">
                  {t(s.energyVendors.text)}
                </p>
              </Reveal>

              <div className="mt-14 grid gap-6 md:grid-cols-3">
                {s.energyVendors.groups.map((group, i) => {
                  const GIcon = groupIcons[i];
                  return (
                    <Reveal key={group.title.en} delay={i * 0.1}>
                      <div className="group flex h-full flex-col rounded-2xl border border-white/10 bg-white/5 p-8 text-start backdrop-blur-sm transition-all duration-300 hover:-translate-y-1.5 hover:border-primary/40">
                        <span className="inline-flex size-14 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                          <GIcon className="size-7" />
                        </span>
                        <h3 className="mt-6 text-xl font-bold text-background">{t(group.title)}</h3>
                        <p className="mt-2.5 text-sm leading-relaxed text-topbar-foreground/70">
                          {t(group.desc)}
                        </p>
                        {group.tags.length > 0 && (
                          <div className="mt-5 flex flex-wrap gap-2">
                            {group.tags.map((tag) => (
                              <span
                                key={tag}
                                className="rounded-full border border-primary/30 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-background/90"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </Reveal>
                  );
                })}
              </div>
            </div>
          </section>
        </>
      )}

      {/* MELT: dark vendor strip -> white overview (AVL only) */}

      {/* OVERVIEW + WHY IT MATTERS */}
      <section className="bg-gradient-to-b from-white to-zinc-50">
        <div className="mx-auto max-w-none w-full px-4 lg:px-8 py-24 sm:py-28">
          <div className="grid items-start gap-12 lg:grid-cols-2">
            <Reveal>
              <span className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">
                {displayLabel} — {t(item.name)}
              </span>
              <h2 className="mt-3 text-3xl font-extrabold text-foreground sm:text-4xl">
                {t(item.heading)}
              </h2>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground">{t(item.text)}</p>
              {isOilGasReg && (
                <p className="mt-5 text-base leading-relaxed text-muted-foreground">
                  {t(item.why[item.why.length - 1].body)}
                </p>
              )}
              {item.slug === "iso-9001" && (
                <div className="mt-6">
                  <h3 className="text-xl font-bold text-foreground">
                    {t({
                      en: "How IraqCert supports preparation",
                      ar: "كيف تدعم عراق سيرت التحضير",
                    })}
                  </h3>
                  <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                    {t(content.faq.services["iso-9001"][3].a)}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-4">
                    <LangLink
                      to="/insights/iso-certification-scope-activities-sites-iraq"
                      className="ic-text-link"
                    >
                      {t({ en: "Define activities and sites", ar: "حدّد الأنشطة والمواقع" })}
                    </LangLink>
                    <LangLink
                      to="/insights/iso-consultant-vs-certification-body-in-iraq"
                      className="ic-text-link"
                    >
                      {t({
                        en: "Preparation and certification roles",
                        ar: "أدوار التحضير ومنح الشهادة",
                      })}
                    </LangLink>
                  </div>
                </div>
              )}
            </Reveal>

            <Reveal delay={0.1}>
              <div className="rounded-3xl border border-slate-100 bg-card p-8 shadow-soft sm:p-10">
                <h3 className="text-xl font-bold text-card-foreground">{t(item.whyTitle)}</h3>
                <ul className="mt-6 space-y-5">
                  {(isOilGasReg ? item.why.slice(0, -1) : item.why).map((point) => (
                    <li key={point.lead.en} className="flex items-start gap-3">
                      <span className="mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                        <Check className="size-3.5" />
                      </span>
                      <span className="text-sm leading-relaxed text-foreground/90">
                        <strong className="font-bold text-foreground">{t(point.lead)} </strong>
                        <span className="text-muted-foreground">{t(point.body)}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {isAvl ? (
        <BuyerGuides registration />
      ) : isOilGasReg ? null : (
        <>
          {/* MELT: white overview -> dark audit process */}

          {/* AUDIT PROCESS */}
          <section className="bg-topbar text-topbar-foreground">
            <div className="mx-auto max-w-none w-full px-4 lg:px-8 py-24 sm:py-28">
              <Reveal className="mx-auto max-w-2xl text-center">
                <span className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">
                  {t(s.auditEyebrow)}
                </span>
                <h2 className="mt-3 text-3xl font-extrabold text-background sm:text-4xl">
                  {t(s.auditTitle)}
                </h2>
                <p className="mx-auto mt-4 max-w-xl text-topbar-foreground/70">
                  {t(s.auditSubtitle)}
                </p>
              </Reveal>

              <div className="mx-auto mt-14 grid max-w-4xl gap-6 md:grid-cols-2">
                {s.audit.stages.map((stage, i) => {
                  const StageIcon = stageIcons[i];
                  return (
                    <Reveal key={stage.title.en} delay={i * 0.1}>
                      <div className="group h-full rounded-2xl border border-white/10 bg-white/5 p-8 text-start backdrop-blur-sm transition-all duration-300 hover:-translate-y-1.5 hover:border-primary/40">
                        <div className="flex items-center gap-4">
                          <span className="inline-flex size-14 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                            <StageIcon className="size-7" />
                          </span>
                          <span className="text-xs font-bold uppercase tracking-widest text-primary">
                            {t(stage.tag)}
                          </span>
                        </div>
                        <h3 className="mt-6 text-xl font-bold text-background">{t(stage.title)}</h3>
                        <p className="mt-2.5 text-sm leading-relaxed text-topbar-foreground/70">
                          {t(stage.text)}
                        </p>
                      </div>
                    </Reveal>
                  );
                })}
              </div>
            </div>
          </section>

          {/* MELT: dark audit process -> white CTA */}
        </>
      )}

      {/* OPERATOR / PLATFORM REGISTRATION GUIDES (registration pages only) */}
      {(isAvl || isOilGasReg) && (
        <section className="bg-background">
          <div className="mx-auto max-w-none w-full px-4 lg:px-8 pb-24 sm:pb-28">
            <Reveal className="mx-auto max-w-2xl text-center">
              <span className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">
                {t({ en: "Operators & Platforms", ar: "الجهات والمنصات" })}
              </span>
              <h2 className="mt-3 text-3xl font-extrabold text-foreground sm:text-4xl">
                {t({ en: "Compare Registration Routes", ar: "قارن مسارات التسجيل" })}
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
                {t({
                  en: "Start with the named buyer and your activity. These guides distinguish the route, requested evidence and decisions that remain with each buyer.",
                  ar: "ابدأ بالجهة المشترية المحددة ونشاطك. توضّح هذه الأدلة المسار والأدلة المطلوبة والقرارات التي تبقى بيد كل جهة مشترية.",
                })}
              </p>
            </Reveal>
            <div className="mx-auto mt-12 grid max-w-5xl gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {vendors.map((v, i) => (
                <Reveal key={v.slug} delay={i * 0.06}>
                  <LangLink
                    to={`/services/oil-gas-registration/${v.slug}`}
                    className="group flex h-full flex-col rounded-2xl border border-slate-100 bg-card p-7 text-start shadow-soft transition-all duration-300 hover:-translate-y-1.5 hover:border-primary/30 hover:shadow-card"
                  >
                    <h3 className="text-lg font-bold text-card-foreground group-hover:text-primary">
                      {t(v.name)}
                    </h3>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                      {t({ en: "Read registration guide", ar: "اقرأ دليل التسجيل" })}
                    </span>
                  </LangLink>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SECTORS THAT NEED THIS STANDARD (ISO standard pages only) */}
      {(() => {
        if (!item.slug.startsWith("iso-")) return null;
        const isoLabel = `ISO ${item.slug.replace("iso-", "")}`;
        const sectors = industries.filter((ind) => ind.standards.includes(isoLabel));
        if (sectors.length < 2) return null;
        return (
          <section className="bg-background">
            <div className="mx-auto max-w-none w-full px-4 lg:px-8 pb-20 sm:pb-24">
              <Reveal className="mx-auto max-w-2xl text-center">
                <span className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">
                  {t({ en: "Who Needs It", ar: "من يحتاجها" })}
                </span>
                <h2 className="mt-3 text-3xl font-extrabold text-foreground sm:text-4xl">
                  {t({
                    en: `Sectors That May Need ${isoLabel} in Iraq`,
                    ar: `قطاعات قد تحتاج إلى شهادة ${isoLabel} في العراق`,
                  })}
                </h2>
              </Reveal>
              <div className="mx-auto mt-10 flex max-w-3xl flex-wrap justify-center gap-3">
                {sectors.map((ind) => (
                  <LangLink
                    key={ind.slug}
                    to={`/industries/${ind.slug}`}
                    className="rounded-full border border-primary/30 bg-primary/10 px-4 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
                  >
                    {t(ind.name)}
                  </LangLink>
                ))}
              </div>
            </div>
          </section>
        );
      })()}

      {!isAvl && <BuyerGuides registration={!isIso} />}
      {/* FAQ */}
      <FaqSection items={content.faq.services[item.slug]} />

      {/* CTA */}
      <section className="bg-gradient-to-b from-background to-accent">
        <div className="mx-auto max-w-none w-full px-4 lg:px-8 py-20">
          <Reveal className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
              {t(s.cta.title)}
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-muted-foreground">{t(s.cta.subtitle)}</p>
            {item.slug === "iso-29001" && (
              <p className="mx-auto mt-5 max-w-2xl text-muted-foreground">
                <LangLink
                  to="/insights/iso-29001-vs-api-q1"
                  className="font-semibold text-primary underline underline-offset-2"
                >
                  {t({
                    en: "Compare ISO 29001, API Q1 and API Q2 before choosing a route",
                    ar: "قارن ISO 29001 وAPI Q1 وAPI Q2 قبل اختيار المسار",
                  })}
                </LangLink>
              </p>
            )}
            {item.slug === "iso-9001" && (
              <p className="mx-auto mt-5 max-w-2xl text-muted-foreground">
                {t({
                  en: "Before requesting a quotation, review the ",
                  ar: "قبل طلب عرض سعر، راجع دليل ",
                })}
                <LangLink
                  to="/insights/iso-certification-cost-and-process-in-iraq"
                  className="font-semibold text-primary underline underline-offset-2"
                >
                  {t({
                    en: "ISO certification cost and process in Iraq",
                    ar: "تكلفة شهادات الأيزو وإجراءاتها في العراق",
                  })}
                </LangLink>
                {t({
                  en: " guide for the information needed to define the scope.",
                  ar: " لمعرفة المعلومات اللازمة لتحديد نطاق العمل.",
                })}
              </p>
            )}
            <a
              href={quotationUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-8 py-3.5 text-base font-semibold text-primary-foreground shadow-soft transition-all hover:brightness-110"
            >
              {isIso
                ? t({
                    en: `Request ${item.label} preparation pricing on WhatsApp`,
                    ar: `اطلب عرض سعر للتحضير لمواصفة ${item.label} عبر واتساب`,
                  })
                : t({
                    en: "Request a registration review on WhatsApp",
                    ar: "اطلب مراجعة مسار التسجيل عبر واتساب",
                  })}
            </a>
          </Reveal>
        </div>
      </section>

      {/* SEAMLESS MELT INTO FOOTER */}
    </>
  );
}
